import { Controller } from "@hotwired/stimulus"

// Fills the briefing in, one watch at a time.
//
// Each item takes 30-60s, so a five-item cockpit would be a five-minute stare at a spinner if
// we generated server-side before rendering. Instead the page renders skeletons immediately and
// this walks the list, replacing each block as its answer lands.
//
// Sequential, not parallel: the engine is the expensive part and firing five at once buys
// nothing but a rate limit. It also means the user reads the first finding while the rest are
// still being worked out.
//
// The work itself runs on Sidekiq — the request that starts it returns immediately, so this
// POLLS the same endpoint until the answer lands. Without that a generation would hold a Puma
// thread for a minute, and five watches would consume every thread the app has.
export default class extends Controller {
  static targets = ["bar", "status"]
  static values  = { urls: Array, total: Number }

  // Measured on live data: 40s for a simple ask, 160s+ for a "why" that probes three rounds.
  // The old 3-minute cap only just cleared the slowest observed run, so a heavier report would
  // have left a skeleton spinning forever while the job quietly finished behind it.
  //
  // Backs off after the first minute: the early polls catch the fast asks, and after that a
  // slower cadence costs the server less without the user noticing.
  static POLL_MS      = 3000
  static SLOW_POLL_MS = 8000
  static FAST_FOR_MS  = 60000
  static MAX_WAIT_MS  = 600000   // 10 minutes, then say so rather than spin

  connect() {
    this.cancelled = false
    this.stalled = false
    this.done = 0
    this.run()
  }

  disconnect() {
    this.cancelled = true
  }

  async run() {
    for (const url of this.urlsValue) {
      if (this.cancelled) return
      await this.fetchUntilReady(url)
      this.done += 1
      this.progress()
    }
    this.finish()
  }

  // The first call enqueues; each later one returns the item as it stands. We stop as soon as
  // the returned block is no longer a skeleton.
  async fetchUntilReady(url) {
    const started = Date.now()
    while (Date.now() - started < this.constructor.MAX_WAIT_MS) {
      if (this.cancelled) return
      const html = await this.fetchOne(url)
      if (html === null) return                      // request failed outright — move on
      if (!html.includes("laz-cw-skeleton")) return  // the answer has landed
      const elapsed = Date.now() - started
      const wait = elapsed < this.constructor.FAST_FOR_MS
        ? this.constructor.POLL_MS
        : this.constructor.SLOW_POLL_MS
      await new Promise(r => setTimeout(r, wait))
    }
    // Gave up waiting. The job may still finish and store its answer, so say that rather than
    // leave a skeleton pulsing with no explanation.
    this.markStalled(url)
  }

  // Replaces the skeleton's status text in place — the item is still valid, we just stopped
  // watching it.
  markStalled(url) {
    const id = new URL(url, window.location.origin).searchParams.get("item_id")
    const el = id && document.querySelector(`[id$="${id}"] .laz-cw-skeleton`)
    if (!el) return
    el.classList.add("is-stalled")
    el.setAttribute("aria-busy", "false")
    el.innerHTML = '<p class="laz-cw-quiet">Still working on this one — reload in a minute to see it.</p>'
    // Suppresses the regroup below: the item is still generating, so a reload would start the
    // same ten-minute wait over and over.
    this.stalled = true
  }

  async fetchOne(url) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        }
      })
      if (!response.ok) return null
      const html = await response.text()
      // Turbo swaps the skeleton for the real block; a failed item keeps its own error state.
      window.Turbo.renderStreamMessage(html)
      return html
    } catch (_) {
      // One item failing must never stop the rest of the briefing.
      return null
    }
  }

  progress() {
    if (this.hasBarTarget && this.totalValue > 0) {
      this.barTarget.style.width = `${Math.round((this.done / this.totalValue) * 100)}%`
    }
    if (this.hasStatusTarget) {
      const left = this.totalValue - this.done
      this.statusTarget.textContent = left > 0 ? `Reading ${left} more…` : "Done"
    }
  }

  // REGROUP ONCE EVERYTHING HAS LANDED.
  //
  // Each answer is swapped into the slot its item occupied AT PAGE LOAD, and that slot was
  // placed using the PREVIOUS run's state. An ask that was quiet yesterday and needs him today
  // therefore renders a full "Needs you" card inside the Quiet list — and the lede above it
  // still reads "All quiet", because the heading, the counts and the chips were all decided
  // from the same stale grouping.
  //
  // Re-rendering costs one cheap request: every item is fresh by now, so #show regroups from
  // stored answers and calls nothing. It cannot loop — with nothing stale the progress element
  // is not rendered, so this controller never connects again.
  finish() {
    if (this.cancelled) return
    this.element.classList.add("is-done")
    // Leave the "Done" state visible briefly so it registers, then get out of the way.
    setTimeout(() => {
      this.element.remove()
      if (!this.cancelled && !this.stalled) {
        window.Turbo.visit(window.location.href, { action: "replace" })
      }
    }, 1200)
  }
}
